import { RootStore } from "./rootStore";
import { reaction, observable, action, runInAction } from "mobx";
import { IShop } from "../models/shop";
import agent from "../api/agent";
import { IContactForm, ISupportDetails } from "../models/support";
import { IDocument, IFAQs } from "../models/document";
import { ErrorLogDto } from "../models/errorlog";
import ErrorLogWS from "../api/errorLog";

export default class CommonStore {
  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    reaction(
      () => this.token,
      (token) => {
        if (token) {
          window.sessionStorage.setItem("mdc_jwt", token);
        } else {
          window.sessionStorage.removeItem("mdc_jwt");
        }
      }
    );
    reaction(
      () => this.shopUID,
      (shop) => {
        if (shop) {
          window.localStorage.setItem("mdc_shop", shop);
        } else {
          window.localStorage.removeItem("mdc_shop");
        }
      }
    );
    reaction(
      () => this.prevRoute,
      (pr) => {
        if (pr) {
          window.sessionStorage.setItem("mdc_pr", pr);
        } else {
          window.sessionStorage.removeItem("mdc_pr");
        }
      }
    );
    reaction(
      () => this.currentShop,
      (cs) => {
        if(cs){
          window.sessionStorage.setItem("mdc_ak",cs.appKey);
          window.sessionStorage.setItem("mdc_sc",cs.shopCode);
        }
        else{
          window.sessionStorage.removeItem("mdc_sc");
          window.sessionStorage.removeItem("mdc_ak");
        }
      }
    )
  }

  @observable token: string | null = window.sessionStorage.getItem("mdc_jwt");
  @observable appLoaded = false;
  @observable invalidShopCode = false;
  @observable validShopCode = true;
  @observable shopUID: string | null = window.localStorage.getItem("mdc_shop");
  @observable shops: IShop[] = [];
  @observable currentShop: IShop | undefined;
  @observable message: string = "";
  @observable prevRoute: string | null = window.sessionStorage.getItem("mdc_pr");
  @observable supportDetails: ISupportDetails | undefined;
  @observable termsURL: string = "";
  @observable privacyURL: string = "";
  @observable faqsURL: string = "";
  @observable loadingDocument: boolean = false;
  @observable document: IDocument | undefined;
  @observable faqs: IFAQs | undefined;
  @observable prefilledPostCode: string | undefined;
  @observable apiError: string | undefined;

  @action setApiError = (error: string | undefined) => {
    this.apiError = error;
  }
  
  @action setPrefilledPostCode = (postCode: string) => {
    this.prefilledPostCode = postCode;
  }
  
  @action setFAQsURL = (url: string) =>{
    this.faqsURL = url;
  }
  @action setTermsURL = (url: string) =>{
    this.termsURL = url;
  }
  
  @action setPrivacyURL = (url: string) =>{
    this.privacyURL = url;
  }

  @action loadTerms = () =>{
    this.loadingDocument = true;
    this.addErrorLog("loadTerms","Function called",false)
    agent.Document.terms().then(doc =>{
      runInAction(() => {
        this.document = doc;
        this.loadingDocument = false;
      })
    }).catch((err) => this.addErrorLog("loadTerms",err))
    .finally(() => this.addErrorLog("loadTerms","Function call end",false))
  }

  @action loadPrivacy = () =>{
    this.loadingDocument = true;
    this.addErrorLog("loadPrivacy","Function called",false)
    agent.Document.privacy().then(doc =>{
      runInAction(() => {
        this.document = doc;
        this.loadingDocument = false;
      })
    }).catch((err) => this.addErrorLog("loadPrivacy",err))
    .finally(() => this.addErrorLog("loadPrivacy","Function call end",false))
  }

  @action loadFAQs = () => {
    this.loadingDocument = true;
    this.addErrorLog("loadFAQs","Function called",false)
    agent.Document.faqs().then(faqs => {
      runInAction(() => {
        this.faqs = faqs;
        this.loadingDocument = false;
      })
    }).catch((err) => this.addErrorLog("loadFAQs",err))
    .finally(() => this.addErrorLog("loadFAQs","Function call end",false))
  }

  @action setPrevRoute = (pRoute: string) =>{
    if(this.prevRoute !== pRoute)
      this.prevRoute = pRoute;
  }
  @action setMessage = (message: string) => {
    this.message = message;
  };
  @action setToken = (token: string | null) => {
    this.token = token;
  };

  @action setAppLoaded = () => {
    this.appLoaded = true;
  };

  @action setInvalidShopCode = () => {
    this.invalidShopCode = true;
  };

  @action setShopUID = (uid: string) => {
    this.shopUID = uid;
  };

  @action CheckShopCodeValid = (uid: string) => {
    this.addErrorLog("CheckShopCodeValid","Function called",false)
    agent.Shop.checkshopuid(uid).then((valid)=>{
      runInAction(() => {
      this.validShopCode = valid;
      this.invalidShopCode = !valid;
      })
      if(this.validShopCode)
      if(this.shopUID !== uid){
        this.setShopUID(uid);
        this.loadShops();
      }
    }).catch((err) => this.addErrorLog("CheckShopCodeValid",err))
    .finally(() => this.addErrorLog("CheckShopCodeValid","Function call end",false));
  }

  @action loadShops = async () => {
    if (this.shopUID) {
      this.addErrorLog("loadShops","Function called",false)
      agent.Shop.list(this.shopUID)
        .then((shops) => {
          runInAction(() => {
            this.shops = shops;
            if (shops.length <= 0) {
              this.setInvalidShopCode();
              this.setAppLoaded();
            }
          });
        })
        .catch((error) => {
          this.setInvalidShopCode();
          this.setAppLoaded();
          this.addErrorLog("loadShops",error)
        })
        .finally(() => this.addErrorLog("loadShops","Function call end",false));
    }
  };

  @action setCurrentShop = () => {
    if (this.shopUID) {
      this.currentShop = this.shops.find(
        (s) => s.uniqueRecId.toLowerCase() === this.shopUID?.toLowerCase()
      );
    }
  };

  @action getSupportDetails = () => {
    if(this.currentShop){
      this.addErrorLog("getSupportDetails","Function called",false)
      agent.Support.supportdetails(this.currentShop.appKey, this.currentShop.shopCode)
      .then((response:ISupportDetails) => {
        runInAction(() => {
          this.supportDetails = response;
        })
      }).catch((err) => this.addErrorLog("getSupportDetails",err))
      .finally(() => this.addErrorLog("getSupportDetails","Function call end",false));
    }
  }

  @action sendContactForm = async (contactForm: IContactForm) : Promise<boolean> => {
    this.addErrorLog("sendContactForm",`Function called with payload: ${JSON.stringify(contactForm)}`,false)
    try{
      return await agent.Support.sendcontactform(contactForm)
    }catch(err){
      this.addErrorLog("sendContactForm",err);
      return false
    }
    finally{
      this.addErrorLog("sendContactForm","Function call end",false)
    }
  }

  @action addErrorLog = async (functionName:string, error: unknown, isError:boolean = true) => {
    if(!isError) 
      if(!this.currentShop?.debugMode) return;
    let custid = this.rootStore.userStore.isLoggedIn ? (this.rootStore.userStore.isCustomer ? this.rootStore.userStore.user?.accountCode : 'guest') : 'not-logged-in';
        const log: ErrorLogDto = {
            key: process.env.REACT_APP_LOG_KEY ?? '',
            logDate: new Date(),
            appName: 'WEBAPP',
            customerId: custid ?? 'not-logged-in',
            functionName: functionName,
            error: this.getErrorMessage(error)
        };
        await ErrorLogWS.addLog(log);
  }

  getErrorMessage(error: unknown) {
      if (error instanceof Error) return  `Error: ${error.message}`
      return String(error)
  }
}
        